import { useState } from 'react'

import { Button } from '@modules/shadcn/ui/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@modules/shadcn/ui/Dialog/Dialog'

import { AI_COLOR_PIN_LIMIT, type ResolvedHistoryTitle } from '../utils/historyDisplay'

export interface PinReplaceCandidate {
  imageId: number
  url?: string
  title: ResolvedHistoryTitle
  cutLabel: string
  createdBy?: string
  createdAt?: string
}

interface PinReplaceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** これからピン留めしたい履歴のタイトル（既定表示を含む） */
  candidateTitle: string
  /** いまピン留めしている履歴（ピン留めした順） */
  pinnedItems: PinReplaceCandidate[]
  /** 選んだ履歴のピンを外して候補をピン留めする。失敗は呼び出し側が通知する */
  onReplace: (replaceImageId: number) => Promise<void> | void
}

/**
 * ピン留めが上限（4件）のときに、外す履歴を選ぶダイアログ（決定事項2。モックの「入れ替えダイアログ」）。
 * エラーで止めず、外す・付けるを1回の操作で済ませる。
 */
export const PinReplaceDialog: React.FC<PinReplaceDialogProps> = ({
  open,
  onOpenChange,
  candidateTitle,
  pinnedItems,
  onReplace,
}) => {
  const [pendingId, setPendingId] = useState<number | null>(null)

  const handleReplace = async (imageId: number) => {
    setPendingId(imageId)
    try {
      await onReplace(imageId)
      onOpenChange(false)
    } catch (error) {
      console.error('[PinReplaceDialog] ピン留めの入れ替えに失敗:', error)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <Dialog onOpenChange={pendingId === null ? onOpenChange : () => {}} open={open}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>ピン留めは1プロジェクトに{AI_COLOR_PIN_LIMIT}件までです</DialogTitle>
          <DialogDescription>
            「{candidateTitle}
            」をピン留めするには、いまピン留めしている履歴を1件外してください。外した履歴は消えません。
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2">
          {pinnedItems.map(item => {
            const meta = [item.cutLabel, item.createdBy, item.createdAt].filter(Boolean).join('・')
            return (
              <li
                className="flex items-center gap-3 rounded-lg border border-input p-2"
                key={item.imageId}
              >
                <div className="h-[54px] w-[72px] shrink-0 overflow-hidden rounded-md bg-muted">
                  {item.url ? (
                    <img alt="" className="h-full w-full object-cover" src={item.url} />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={
                      item.title.isDefault
                        ? 'truncate text-sm text-muted-foreground'
                        : 'truncate text-sm font-semibold'
                    }
                  >
                    {item.title.text}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {item.title.isDefault ? `${item.cutLabel}・タイトル未設定` : meta}
                  </p>
                </div>
                <Button
                  disabled={pendingId !== null}
                  onClick={() => void handleReplace(item.imageId)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {pendingId === item.imageId ? '入れ替え中...' : '外して入れ替える'}
                </Button>
              </li>
            )
          })}
        </ul>
        <DialogFooter className="sm:justify-end">
          <Button
            disabled={pendingId !== null}
            onClick={() => onOpenChange(false)}
            type="button"
            variant="outline"
          >
            キャンセル
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
